// src/pages/BuyerLaunchPanel.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const APPLICATIONS = {
  PART1: {
    id: "PART1",
    name: "MISSING: The Search for Terry Hughes",
    shortName: "MISSING Part I",
    description: "As an investigator, you'll search for Terry Hughes who has disappeared. Navigate complex situations, collect evidence, while learning to recognize and mitigate confirmation bias, the fundamental attribution error, and bias blind spot that can affect your judgment."
  },
  PART2: {
    id: "PART2",
    name: "MISSING: The Final Secret",
    shortName: "MISSING Part II",
    description: "Continue your journey in cognitive bias training with new challenges and scenarios, learning to address anchoring bias, the representativeness heuristic, and projection bias."
  }
};

const BuyerLaunchPanel = () => {
  const navigate = useNavigate();
  const [userProgress, setUserProgress] = useState({});
  const [availableApps, setAvailableApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [selectedApp, setSelectedApp] = useState("PART1"); // Default to Part I
  const [buyerEmail, setBuyerEmail] = useState("");

  // Format time from seconds to hours and minutes
  const formatPlayTime = (seconds) => {
    if (!seconds && seconds !== 0) return 'Never played';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Fetch buyer email and available applications
  useEffect(() => {
    const fetchBuyerData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/buyer');
          return;
        }

        // Get buyer details
        const response = await fetch("/buyer/details", {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error fetching details: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Store buyer email
        setBuyerEmail(data.BuyerEmail);
        
        // Find self-assigned applications
        const apps = [];
        
        if (data.Users && data.Users.length > 0) {
          // Look for the buyer's own email in the Users array
          const selfUser = data.Users.find(user => user.userEmail === data.BuyerEmail);
          
          if (selfUser) {
            // Get applications assigned to self
            const userApps = selfUser.applications || ["PART1"];
            
            // Only include apps that exist in our APPLICATIONS config
            const validApps = userApps.filter(app => APPLICATIONS[app]);
            
            if (validApps.length > 0) {
              apps.push(...validApps);
              // If the user has access to Part I and has completed it, default to Part II
              if (validApps.includes("PART1") && validApps.includes("PART2")) {
                const part1Progress = await fetchProgressForApp("PART1");
                if (part1Progress && part1Progress.gameComplete) {
                  setSelectedApp("PART2");
                }
              }
            }
          }
        }
        
        if (apps.length > 0) {
          setAvailableApps(apps);
        } else {
          // If no apps found, redirect back to buyer dashboard
          navigate('/buyer');
          return;
        }
        
        // Fetch progress for the default selected app
        const initialApp = apps.includes("PART2") && 
                           apps.includes("PART1") ? 
                           (selectedApp || "PART1") : 
                           apps[0];
        
        await fetchProgressForApp(initialApp);
        
      } catch (err) {
        console.error('Error fetching buyer data:', err);
        setError('Could not load your available applications and progress. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBuyerData();
  }, [navigate]);

  // Fetch progress for a specific application
  const fetchProgressForApp = async (application) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      const progressUrl = `/user/progress?application=${application}`;
      
      const progressResponse = await fetch(progressUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (progressResponse.ok) {
        const responseData = await progressResponse.json();
        
        if (responseData && responseData.progress) {
          setUserProgress(prev => ({
            ...prev,
            [application]: responseData.progress
          }));
          
          return responseData.progress;
        }
      }
      
      return null;
    } catch (err) {
      console.error(`Error fetching progress for ${application}:`, err);
      return null;
    }
  };

  // Launch the selected game
  // In BuyerLaunchPanel.js, modify the launchGame function
const launchGame = async () => {
  setLaunching(true);
  setError(null);

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/buyer');
      return;
    }

    // Use the dedicated endpoint for buyer self-launches
    const response = await fetch('/buyer/launch-game', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        application: selectedApp
      })
    });
  
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.reason || errorData.error || 'Failed to launch game');
    }

    const data = await response.json();
    window.location.href = data.streamingUrl; // Redirect to AppStream
  } catch (err) {
    console.error('Error launching game:', err);
    setError(`Could not launch ${APPLICATIONS[selectedApp].shortName}: ${err.message}`);
  } finally {
    setLaunching(false);
  }
};

  // Function to show certificate link for completed games
  const showCertificateLink = () => {
    const currentProgress = userProgress[selectedApp];
    
    if (currentProgress && currentProgress.gameComplete) {
      return (
        <div className="certificate-section" style={{
          marginTop: "1rem",
          padding: "1rem",
          backgroundColor: "#e8f5e9",
          borderRadius: "4px"
        }}>
          <h3 style={{ margin: "0 0 0.5rem 0" }}>Congratulations on completing {APPLICATIONS[selectedApp].shortName}!</h3>
          <p style={{ margin: "0 0 1rem 0" }}>You have successfully completed the training and learned valuable skills in recognizing cognitive biases.</p>
          {buyerEmail ? (
            <a 
              href={`https://admin.cretecinc.com/certificate-name?email=${encodeURIComponent(buyerEmail)}&application=${selectedApp}`}
              style={{
                display: "inline-block",
                padding: "0.5rem 1rem",
                backgroundColor: "#4CAF50",
                color: "white",
                textDecoration: "none",
                borderRadius: "4px"
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              Get Your Certificate
            </a>
          ) : (
            <p style={{ color: "#f44336" }}>
              Unable to retrieve your email. Please check your email for a certificate link or contact support.
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div style={{ padding: "1rem", maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  // Get current application details
  const currentApp = APPLICATIONS[selectedApp];
  const currentProgress = userProgress[selectedApp];
  
  return (
    <div style={{ padding: "1rem", maxWidth: 700, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h1 style={{ fontSize: "1.5rem", margin: 0 }}>Launch Application</h1>
        <button 
          onClick={() => navigate("/buyer")}
          style={{ 
            padding: "0.5rem 1rem", 
            backgroundColor: "#2196F3", 
            color: "white", 
            border: "none",
            borderRadius: "4px",
            cursor: "pointer" 
          }}
        >
          Back to Dashboard
        </button>
      </div>
      
      {availableApps.length > 1 && (
        <div style={{ 
          border: "1px solid #ccc", 
          padding: "1rem", 
          marginBottom: "1rem",
          borderRadius: "4px" 
        }}>
          <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Select Application</h2>
          <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
            {availableApps.map(appId => (
              <button
                key={appId}
                onClick={() => {
                  setSelectedApp(appId);
                  fetchProgressForApp(appId);
                }}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: selectedApp === appId ? "#4CAF50" : "#e0e0e0",
                  color: selectedApp === appId ? "white" : "black",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: selectedApp === appId ? "bold" : "normal"
                }}
              >
                {APPLICATIONS[appId].shortName}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h1 style={{ fontSize: "1.3rem", margin: "0 0 1rem 0" }}>{currentApp.name}</h1>
        
        <div style={{ marginBottom: "1rem" }}>
          <h2 style={{ fontSize: "1.1rem", margin: "0 0 0.5rem 0" }}>Your Progress</h2>
          
          {currentProgress ? (
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "1fr 1fr", 
              gap: "0.5rem",
              backgroundColor: "#f5f5f5",
              padding: "1rem",
              borderRadius: "4px"
            }}>
              <div><strong>Play Time:</strong></div>
              <div>{formatPlayTime(currentProgress.playTime)}</div>
              
              <div><strong>Current Checkpoint:</strong></div>
              <div>{currentProgress.checkPoint}</div>
              
              <div><strong>AAR Checkpoint:</strong></div>
              <div>{currentProgress.aarCheckpoint}</div>
              
              <div><strong>Last Played:</strong></div>
              <div>{formatDate(currentProgress.lastUpdated)}</div>
              
              <div><strong>Status:</strong></div>
              <div style={{ color: currentProgress.gameComplete ? "green" : "#ff9800", fontWeight: "bold" }}>
                {currentProgress.gameComplete ? "Completed" : "In Progress"}
              </div>
            </div>
          ) : (
            <div>
              <p>You haven't started {currentApp.shortName} yet. Click the button below to begin your investigation.</p>
            </div>
          )}
        </div>
        
        {error && (
          <div style={{ 
            color: "red", 
            marginBottom: "1rem", 
            padding: "0.5rem", 
            backgroundColor: "#ffebee",
            borderRadius: "4px" 
          }}>
            {error}
          </div>
        )}
        
        {currentProgress && currentProgress.gameComplete ? (
          <div>
            <p style={{ color: "green", fontWeight: "bold" }}>
              You have completed {currentApp.shortName}! Thank you for participating in the training.
            </p>
            {showCertificateLink()}
          </div>
        ) : (
          <button 
            onClick={launchGame} 
            disabled={launching}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: launching ? "default" : "pointer",
              fontSize: "1rem",
              fontWeight: "bold",
              width: "100%"
            }}
          >
            {launching ? 'Launching...' : currentProgress ? `Resume ${currentApp.shortName}` : `Start ${currentApp.shortName}`}
          </button>
        )}
      </div>
      
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem",
        borderRadius: "4px"
      }}>
        <h3 style={{ fontSize: "1.1rem", margin: "0 0 0.5rem 0" }}>About {currentApp.name}</h3>
        <p style={{ margin: "0" }}>{currentApp.description}</p>
      </div>
      
      <div style={{ marginTop: "2rem", textAlign: "center", fontSize: "0.9rem" }}>
        Need help?{" "}
        <a 
          href="mailto:support@creativetechnologiesincla.zohodesk.com" 
          style={{ color: "#2196F3", textDecoration: "none" }}
        >
          Contact Support
        </a>
      </div>
    </div>
  );
};

export default BuyerLaunchPanel;